import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Search, X, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { galleryCategories, galleryImages } from '../data/gallery';

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay, ease: [0.23, 1, 0.32, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function GalleryPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const filtered = activeFilter === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeFilter);

  const openLightbox = useCallback((img) => setLightbox(img), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  const navigateLightbox = useCallback((dir) => {
    if (!lightbox) return;
    const idx = filtered.findIndex(img => img.id === lightbox.id);
    const next = (idx + dir + filtered.length) % filtered.length;
    setLightbox(filtered[next]);
  }, [lightbox, filtered]);

  return (
    <main data-testid="gallery-page">
      {/* Hero */}
      <section className="relative h-[70vh] overflow-hidden bg-[#0A1A27]">
        <div className="absolute right-0 top-0 w-1/2 h-full hidden lg:block">
          <img src="https://customer-assets.emergentagent.com/job_template-astud/artifacts/p2firt48_Auteur-2-scaled.webp" alt="" className="w-full h-full object-cover object-top opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1A27] to-transparent" />
        </div>
        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
            <div className="max-w-xl">
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-montserrat text-xs uppercase tracking-[0.3em] text-[#7DD4F5] mb-4">
                Yannick Gouguenheim
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-playfair text-5xl md:text-6xl text-white tracking-tight mb-4">
                L'œil sous la surface
              </motion.h1>
              <div className="w-16 h-0.5 bg-[#2B7BA8] mb-6" />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="font-lato text-lg text-[#8CA1B0] leading-relaxed mb-8">
                Explorez des univers aquatiques rarement observés — entre lumière, silence et biodiversité.
              </motion.p>
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                onClick={() => document.getElementById('gallery-grid')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="gallery-explore-btn"
                className="px-8 py-3.5 rounded-full bg-[#2B7BA8] text-white font-montserrat text-sm uppercase tracking-wider hover:bg-[#5AACE0] transition-all duration-300"
              >
                Explorer la galerie
              </motion.button>
            </div>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <div className="sticky top-[68px] z-30 bg-[#0A1A27]/95 backdrop-blur-xl border-b border-white/5" id="gallery-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {galleryCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`filter-pill px-5 py-2 rounded-full font-montserrat text-xs uppercase tracking-wider whitespace-nowrap ${
                  activeFilter === cat.id ? 'active' : 'text-[#8CA1B0]'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery grid - masonry */}
      <section className="py-12 bg-[#0A1A27]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="masonry-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((img, i) => (
                <motion.div
                  key={img.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
                  className="masonry-item"
                >
                  <div
                    className="gallery-item rounded-2xl overflow-hidden cursor-pointer"
                    onClick={() => openLightbox(img)}
                    data-testid={`gallery-image-${img.id}`}
                  >
                    <img
                      src={img.src}
                      alt={img.title}
                      className="w-full object-cover"
                      style={{ height: i % 3 === 0 ? '350px' : i % 3 === 1 ? '280px' : '320px' }}
                      loading="lazy"
                    />
                    <div className="gallery-overlay rounded-2xl items-center justify-center">
                      <Search size={20} className="text-[#7DD4F5]" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 cta-parallax relative"
        style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_template-astud/artifacts/prq4obov__DSC4113.jpg')` }}
      >
        <FadeIn className="max-w-3xl mx-auto text-center px-6 relative z-10">
          <h2 className="font-playfair text-3xl md:text-4xl text-white mb-4">Ces images vous inspirent pour un projet ?</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/contact" data-testid="gallery-cta-contact" className="px-8 py-3.5 rounded-full bg-white text-[#0D2B3E] font-montserrat text-sm uppercase tracking-wider hover:bg-[#F5F7F9] transition-all duration-300 font-semibold">
              Me contacter
            </Link>
            <a href="https://cloud.leviia.com/s/ZnIy.5e5BoqxBHTS94Ex" target="_blank" rel="noopener noreferrer" className="px-8 py-3.5 rounded-full border-2 border-white/30 text-white font-montserrat text-sm uppercase tracking-wider hover:bg-white/10 transition-all duration-300 inline-flex items-center gap-2">
              Découvrez ma galerie en intégralité <ArrowRight size={14} />
            </a>
          </div>
        </FadeIn>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lightbox-backdrop"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <button onClick={closeLightbox} data-testid="lightbox-close" className="absolute top-6 right-6 text-white/60 hover:text-white z-10">
              <X size={28} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              data-testid="lightbox-prev"
              className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all z-10"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              data-testid="lightbox-next"
              className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all z-10"
            >
              <ChevronRight size={24} />
            </button>

            <div onClick={e => e.stopPropagation()} className="max-w-5xl max-h-[85vh] mx-6">
              <motion.img
                key={lightbox.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                src={lightbox.src}
                alt={lightbox.title}
                className="max-w-full max-h-[75vh] object-contain rounded-xl"
              />
              <div className="text-center mt-4">
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
